import { z } from 'zod';
import { paOperationSemanticsSchema } from './pa-operation-semantics';

// ============================================================================
// Task #3844 — Delega consapevole dei poteri del Personal Assistant.
//
// DTO risposta condivisi BE↔FE per gli endpoint:
//   - GET  /personal-assistant/agents/:id/delegation        → PaDelegationStatusDto
//   - POST /personal-assistant/agents/:id/delegation        → PaDelegationStatusDto
//   - GET  /personal-assistant/agents/:id/delegation/audit  → PaDelegationAuditListDto
//
// Convenzioni wire: timestamp → union string|Date (pg serializza Date, il JSON
// wire è string); colonne NULLABLE → .nullable().
// ============================================================================

/** Modalità di delega persistita su `ai_personal_assistants.delegation_mode`. */
export const paDelegationModeSchema = z.enum(['delegated', 'intersection']);
export type PaDelegationMode = z.infer<typeof paDelegationModeSchema>;

export const paToolAvailabilitySchema = z.object({
  server_id: z.string(),
  tool_name: z.string(),
  selected: z.boolean(),
  bulk_allowed: z.boolean().nullable(),
  operation_semantics: paOperationSemanticsSchema.nullable(),
  availability: z.enum(['available', 'delegated', 'disabled', 'role_denied', 'unverifiable']),
  required_permission: z.string().nullable(),
  reason: z.string().nullable(),
});
export type PaToolAvailability = z.infer<typeof paToolAvailabilitySchema>;

const wireTimestamp = z.union([z.string(), z.date()]);

/**
 * Un tool dell'agente che l'utente assegnato NON può usare nell'app
 * (il suo ruolo RBAC non ha `required_permission`).
 */
export const paDelegationDiffEntrySchema = z.object({
  server_id: z.string().optional(),
  tool_name: z.string(),
  category: z.string().nullable(),
  required_permission: z.string(),
});
export type PaDelegationDiffEntry = z.infer<typeof paDelegationDiffEntrySchema>;

/**
 * Stato completo della delega di un PA: modalità corrente, conferma admin,
 * diff corrente (tool oltre i permessi dell'utente assegnato) e drift rispetto
 * allo snapshot confermato.
 */
export const paDelegationStatusSchema = z.object({
  assistant_id: z.string(),
  assigned_user_id: z.string().nullable(),
  assigned_user_name: z.string().nullable(),
  /** true = l'utente assegnato è admin/super_admin → diff sempre vuoto. */
  user_is_privileged: z.boolean(),
  mode: paDelegationModeSchema,
  ack_at: wireTimestamp.nullable(),
  ack_by: z.string().nullable(),
  ack_by_name: z.string().nullable(),
  /** true = il diff corrente differisce dallo snapshot confermato (o mai confermato con diff non vuoto). */
  drift: z.boolean(),
  /** Hash sha256 del diff corrente (contenuto canonico ordinato). */
  current_hash: z.string(),
  /** Hash confermato dall'admin (null = mai confermato). */
  acked_hash: z.string().nullable(),
  /** Tool dell'agente OLTRE i permessi RBAC dell'utente assegnato. */
  beyond_tools: z.array(paDelegationDiffEntrySchema),
  /** Numero totale di tool MCP abilitati per l'agente del PA. */
  total_tools: z.number(),
  diagnostic: z.string().nullable().optional(),
  tools: z.array(paToolAvailabilitySchema).optional(),
});
export type PaDelegationStatusDto = z.infer<typeof paDelegationStatusSchema>;

export const paDelegationStatusResponseSchema = z.object({
  success: z.boolean(),
  data: paDelegationStatusSchema,
});
export type PaDelegationStatusResponse = z.infer<typeof paDelegationStatusResponseSchema>;

/** Body di POST /agents/:id/delegation. */
export const paDelegationAckBodySchema = z.object({
  mode: paDelegationModeSchema,
  expected_hash: z.string().regex(/^[a-f0-9]{64}$/),
});
export type PaDelegationAckBody = z.infer<typeof paDelegationAckBodySchema>;

export const paDelegationAuditEventTypeSchema = z.enum(['mode_ack', 'delegated_action', 'drift_detected']);
export type PaDelegationAuditEventType = z.infer<typeof paDelegationAuditEventTypeSchema>;

export const paDelegationAuditEntrySchema = z.object({
  id: z.string(),
  event_type: paDelegationAuditEventTypeSchema,
  assigned_user_id: z.string().nullable(),
  assigned_user_name: z.string().nullable(),
  actor_user_id: z.string().nullable(),
  actor_user_name: z.string().nullable(),
  tool_name: z.string().nullable(),
  required_permission: z.string().nullable(),
  detail: z.record(z.unknown()).nullable(),
  created_at: wireTimestamp,
});
export type PaDelegationAuditEntry = z.infer<typeof paDelegationAuditEntrySchema>;

export const paDelegationAuditListSchema = z.object({
  items: z.array(paDelegationAuditEntrySchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});
export type PaDelegationAuditListDto = z.infer<typeof paDelegationAuditListSchema>;

export const paDelegationAuditListResponseSchema = z.object({
  success: z.boolean(),
  data: paDelegationAuditListSchema,
});
export type PaDelegationAuditListResponse = z.infer<typeof paDelegationAuditListResponseSchema>;
